import type { BidAssociation, DsmScopeItem } from "design-scope";

/**
 * Append `toAdd` bids onto a row's existing list, skipping duplicates that
 * already share the same bidPackageId. If any added bid is primary, that bid
 * package becomes the sole primary after merge; otherwise existing primary
 * flags stay.
 */
export function appendBidAssociations(
  row: DsmScopeItem,
  toAdd: BidAssociation[]
): BidAssociation[] {
  const pending = toAdd.filter((b) => b.bidPackageId);
  const seen = new Set(row.bids.map((b) => b.bidPackageId).filter(Boolean));
  const merged = [...row.bids];
  for (const b of pending) {
    if (seen.has(b.bidPackageId)) continue;
    seen.add(b.bidPackageId);
    merged.push(b);
  }

  const primaryId = pending.find((b) => b.primary)?.bidPackageId ?? null;
  if (!primaryId || !merged.some((b) => b.bidPackageId === primaryId)) {
    return merged;
  }

  return merged.map((b) => ({
    ...b,
    primary: b.bidPackageId === primaryId
  }));
}

/**
 * Upsert form bids onto a row: replace existing entries with the same
 * bidPackageId, otherwise append. If any form bid is primary, that id becomes
 * the sole primary on the merged row.
 */
export function upsertBidAssociations(
  row: DsmScopeItem,
  formBids: BidAssociation[]
): BidAssociation[] {
  const pending = formBids.filter((b) => b.bidPackageId);
  const merged = [...row.bids];
  for (const b of pending) {
    const idx = merged.findIndex((x) => x.bidPackageId === b.bidPackageId);
    if (idx >= 0) {
      merged[idx] = b;
    } else {
      merged.push(b);
    }
  }

  const primaryId = pending.find((b) => b.primary)?.bidPackageId ?? null;
  if (!primaryId || !merged.some((b) => b.bidPackageId === primaryId)) {
    return merged;
  }

  return merged.map((b) => ({
    ...b,
    primary: b.bidPackageId === primaryId
  }));
}
